import React, { useMemo, useEffect, useRef } from "react";
import "./EventList.css";
import { randomThinkingWord } from "./thinkingWords";
import { randomSparkVariant } from "./sparkVariants";
import UserMessageItem from "./events/UserMessageItem";
import AgentMessageItem from "./events/AgentMessageItem";
import ThinkingItem from "./events/ThinkingItem";
import CompactionNotice from "./events/CompactionNotice";
import ErrorItem from "./events/ErrorItem";
import ToolCallCard from "./events/ToolCallCard";
import SkillLoadBanner from "./events/SkillLoadBanner";
import TypingIndicator from "./events/TypingIndicator";

// Every flavor of tool-use / tool-result the backend can emit. Custom tools
// (the env_* file tools) come through as agent.custom_tool_use with their
// result posted back as user.custom_tool_result.
const TOOL_USE_TYPES = new Set([
  "agent.tool_use", "agent.mcp_tool_use", "agent.custom_tool_use",
]);
const TOOL_RESULT_TYPES = new Set([
  "agent.tool_result", "agent.mcp_tool_result",
  "agent.custom_tool_result", "user.custom_tool_result",
  // Self-hosted built-in tools: our fleet worker posts the result as
  // user.tool_result (vs Anthropic's agent.tool_result in cloud). Without this,
  // the tool card never pairs with its result and spins "running" forever.
  "user.tool_result",
]);

// Item kinds that belong to a single agent response run. Only the first item
// in a contiguous run renders the Claude logo.
const AGENT_SIDE = new Set(["agent", "thinking", "tool"]);

// Pair each tool_use with its result. Match by tool_use_id when both sides
// carry it; otherwise fall back to order (the next unconsumed result that
// occurs after the tool_use). The fallback is what keeps cards from getting
// stuck "running" forever when an id is missing or doesn't line up.
function pairToolResults(events) {
  const results = events.filter((e) => TOOL_RESULT_TYPES.has(e.type));
  const byId = new Map();
  for (const r of results) {
    const id = r.payload?.tool_use_id;
    if (id) byId.set(id, r);
  }
  const consumed = new Set();
  const pair = new Map(); // tool_use event_id -> result event
  // Pass 1: explicit id match.
  for (const e of events) {
    if (!TOOL_USE_TYPES.has(e.type)) continue;
    const id = e.payload?.tool_use_id;
    if (id && byId.has(id)) {
      const r = byId.get(id);
      if (!consumed.has(r)) { pair.set(e.event_id, r); consumed.add(r); }
    }
  }
  // Pass 2: order fallback for any still-unmatched tool_use.
  for (const e of events) {
    if (!TOOL_USE_TYPES.has(e.type)) continue;
    if (pair.has(e.event_id)) continue;
    const r = results.find((x) => !consumed.has(x) && x.seq > e.seq);
    if (r) { pair.set(e.event_id, r); consumed.add(r); }
  }
  return pair;
}

// Closed thinking rows shorter than this are hidden: a tool loop would
// otherwise fill the timeline with "Thought for 2s" lines.
const MIN_THOUGHT_MS = 5000;
const ts = (e) => (e.processed_at ? Date.parse(e.processed_at) : Date.now());

// Exported for tests.
export function buildItems(events, pair = pairToolResults(events)) {
  const items = [];
  // The thinking row for the model request in progress. Opens on
  // span.model_request_start and closes on the first of: agent.thinking (its
  // timestamp marks the end of the thinking block — the event itself carries
  // no reasoning text, Managed Agents never exposes it), the first text or
  // tool call of the request, or span.model_request_end. Once closed it
  // collapses to "Thought for …"; output tokens attach at request end.
  let thinking = null;
  const closeThinking = (e) => { if (thinking && !thinking.endedAt) thinking.endedAt = ts(e); };
  for (const e of events) {
    if (TOOL_USE_TYPES.has(e.type)) {
      closeThinking(e);
      const result = pair.get(e.event_id);
      items.push({ kind: "tool", key: `${e.seq}-${e.event_id}`, event: e, result });
      continue;
    }
    if (TOOL_RESULT_TYPES.has(e.type)) continue; // absorbed into the tool card
    switch (e.type) {
      case "user.message": {
        thinking = null;
        // Hidden setup turns injected at thread start (file-handling guidance).
        if (e.payload?.system_setup) break;
        if (e.payload?.skill_load) {
          items.push({ kind: "skill_load", key: `${e.seq}-${e.event_id}`, event: e });
          break;
        }
        // Skip empty user turns (e.g. tool-result / echo turns the backend
        // records with no text or attachments) — they'd render as an invisible
        // bubble that still adds bottom margin.
        const hasText = (e.payload?.text || "").trim().length > 0;
        const hasAttachments = (e.payload?.attachments || []).length > 0;
        if (hasText || hasAttachments) {
          items.push({ kind: "user", key: `${e.seq}-${e.event_id}`, event: e });
        }
        break;
      }
      case "agent.message":
        if (e.payload?.text) {
          closeThinking(e);
          // Keyed by event_id alone so a live preview and the buffered message
          // that replaces it keep the same component (and typewriter position).
          items.push({ kind: "agent", key: e.event_id, event: e });
        }
        break;
      case "span.model_request_start":
        thinking = { kind: "thinking", key: `${e.seq}-${e.event_id}`, startedAt: ts(e), endedAt: null, outputTokens: null };
        items.push(thinking);
        break;
      case "agent.thinking":
        closeThinking(e);
        break;
      case "span.model_request_end":
        if (thinking) {
          closeThinking(e);
          thinking.outputTokens = e.payload?.output_tokens ?? null;
          thinking = null;
        }
        break;
      case "agent.thread_context_compacted":
        items.push({ kind: "notice", key: `${e.seq}-compact` });
        break;
      case "session.error":
        // MCP auth failures already surface as the yellow connector warning in
        // the sidebar — don't double up with a red error in the chat.
        if (e.payload?.error_type !== "mcp_authentication_failed_error") {
          items.push({ kind: "error", key: `${e.seq}-err`, event: e });
        }
        break;
      default:
        // status / other span / unknown events: nothing to render
        break;
    }
  }
  return items.filter((it) => it.kind !== "thinking" || !it.endedAt || it.endedAt - it.startedAt >= MIN_THOUGHT_MS);
}

export default function EventList({ events, replaying, loading = false, status, initializing = false, pendingConfirmationIds = null, onConfirmTool }) {
  const pair = useMemo(() => pairToolResults(events), [events]);
  const items = useMemo(() => buildItems(events, pair), [events, pair]);
  // A tool is in flight only if its card has no paired result yet.
  const hasRunningTool = items.some((it) => it.kind === "tool" && !it.result);
  const last = items[items.length - 1];
  // A brand-new chat's WebSocket fires an empty replay.start/end while it's
  // initializing. Ignore that here so we don't flash the "Loading earlier
  // messages…" banner or drop the working indicator during init → running; the
  // only thing that should change is the status in the header.
  const effectiveReplaying = replaying && !initializing;
  // Show the loading animation from the moment a thread is selected (before the
  // WS replay even starts) until its history has arrived — never the empty
  // "Send a message to begin" text while messages are still on the way.
  const isLoading = (loading || effectiveReplaying) && !initializing;
  // A single stable "Thinking…" row appears between steps: after the user
  // message, while reasoning, or right after a tool finishes — but NOT while a
  // tool is in flight (its running card shows that) or while the agent message
  // is streaming (the cursor shows that). `initializing` counts as working so
  // the indicator stays continuous before the live "running" status arrives.
  const showWorking =
    (status === "running" || initializing) && !effectiveReplaying && !hasRunningTool &&
    (!last || last.kind === "user" || last.kind === "tool" ||
      last.kind === "skill_load" || last.kind === "notice" ||
      (last.kind === "thinking" && !!last.endedAt)); // thought, now writing

  // Keep the playful working word stable for the whole user turn. The turn is
  // identified by the last user message's id, which is stable across the
  // optimistic→canonical hand-off (shared idempotencyKey) and the init→running
  // transition — so the word no longer changes when the indicator briefly
  // remounts. A new user turn rolls a new word.
  const lastUserKey = useMemo(() => {
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i].kind === "user") return items[i].event.event_id;
    }
    return null;
  }, [items]);
  const workingWordRef = useRef(randomThinkingWord());
  const sparkVariantRef = useRef(randomSparkVariant());
  const turnKeyRef = useRef(lastUserKey);
  if (lastUserKey !== turnKeyRef.current) {
    turnKeyRef.current = lastUserKey;
    workingWordRef.current = randomThinkingWord();
    sparkVariantRef.current = randomSparkVariant(); // new turn, new spark animation
  }
  // Roll a fresh spark each time we start loading earlier messages, so the
  // loading animation varies every time a conversation is opened.
  const replaySparkRef = useRef(randomSparkVariant());
  const wasLoadingRef = useRef(isLoading);
  if (isLoading && !wasLoadingRef.current) {
    replaySparkRef.current = randomSparkVariant();
  }
  wasLoadingRef.current = isLoading;
  // ---- Stick-to-bottom autoscroll --------------------------------------
  // Goal: while parked at the bottom, follow ALL new content (new items AND the
  // streaming typewriter, which grows the last message's height without adding
  // items). If the user scrolls up even slightly, hand scrolling back to them;
  // when they return to the bottom, resume following.
  const scrollRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const lastTopRef = useRef(0);
  const touchYRef = useRef(0);

  // How close to the bottom (px) still counts as "at the bottom". Small, so a
  // deliberate scroll-up releases follow, but >0 to tolerate sub-pixel rounding
  // from fractional content heights.
  const AT_BOTTOM_PX = 4;

  // User intent to move up — wheel/trackpad and touch fire BEFORE our follow can
  // re-pin, so releasing here means the follow never fights the user.
  const onWheel = (e) => { if (e.deltaY < 0) stickToBottomRef.current = false; };
  const onTouchStart = (e) => { touchYRef.current = e.touches?.[0]?.clientY ?? 0; };
  const onTouchMove = (e) => {
    const y = e.touches?.[0]?.clientY ?? 0;
    if (y > touchYRef.current + 2) stickToBottomRef.current = false; // finger down → content up
    touchYRef.current = y;
  };
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const top = el.scrollTop;
    const dist = el.scrollHeight - top - el.clientHeight;
    if (dist <= AT_BOTTOM_PX) {
      stickToBottomRef.current = true;       // back at the very bottom → follow
    } else if (top < lastTopRef.current - 1) {
      stickToBottomRef.current = false;      // moved up (e.g. scrollbar drag) → release
    }
    lastTopRef.current = top;
  };

  // Follow on any content growth. MutationObserver catches both new items
  // (childList) and the typewriter (characterData) — decoupled from `status`,
  // so the tail of a long reply keeps following even if the agent goes idle
  // while the text is still animating in. Instant pin (no smooth) tracks each
  // frame without stutter.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const follow = () => { if (stickToBottomRef.current) el.scrollTop = el.scrollHeight; };
    follow();
    const mo = new MutationObserver(follow);
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    // Re-pin on viewport/layout changes (window resize, side panels toggling).
    const ro = new ResizeObserver(follow);
    ro.observe(el);
    return () => { mo.disconnect(); ro.disconnect(); };
  }, []);

  // A fresh / cleared timeline (new chat, thread switch) should start pinned to
  // the bottom again, regardless of where the user left the previous thread.
  const isEmpty = items.length === 0;
  useEffect(() => { if (isEmpty) stickToBottomRef.current = true; }, [isEmpty]);

  return (
    <div
      className="ca-events"
      ref={scrollRef}
      onScroll={handleScroll}
      onWheel={onWheel}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
    >
      {isLoading && (
        <div className="ev-replay" role="status" aria-label="Loading earlier messages">
          <span className={`ca-spark${replaySparkRef.current ? ` ca-spark--${replaySparkRef.current}` : ""}`}>
            <span className="ca-spark-frames" />
          </span>
        </div>
      )}
      {items.map((it, idx) => {
        // Only the first item in a contiguous agent run shows the Claude logo;
        // the rest keep an empty avatar slot so they stay aligned.
        const prev = items[idx - 1];
        const showAvatar = !(prev && AGENT_SIDE.has(prev.kind));
        switch (it.kind) {
          case "user":     return <UserMessageItem key={it.key} event={it.event} />;
          case "skill_load": return <SkillLoadBanner key={it.key} event={it.event} />;
          case "agent":    return <AgentMessageItem key={it.key} event={it.event} showAvatar={showAvatar} />;
          case "thinking": return <ThinkingItem key={it.key} item={it} running={status === "running" && !it.endedAt} showAvatar={showAvatar} />;
          case "tool": {
            const ev = it.event;
            const awaitingConfirmation = !it.result
              && !!pendingConfirmationIds
              && pendingConfirmationIds.has(ev.event_id)
              && (ev.type === "agent.tool_use" || ev.type === "agent.mcp_tool_use");
            return (
              <ToolCallCard
                key={it.key}
                event={ev}
                result={it.result}
                showAvatar={showAvatar}
                awaitingConfirmation={awaitingConfirmation}
                onConfirm={onConfirmTool}
              />
            );
          }
          case "notice":   return <CompactionNotice key={it.key} />;
          case "error":    return <ErrorItem key={it.key} event={it.event} />;
          default:         return null;
        }
      })}
      {showWorking && (
        <TypingIndicator
          word={workingWordRef.current}
          spark={sparkVariantRef.current}
          showAvatar={!(last && AGENT_SIDE.has(last.kind))}
        />
      )}
      {items.length === 0 && !isLoading && !showWorking && (
        <div className="ev-empty">Send a message to begin.</div>
      )}
    </div>
  );
}
